import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Info,
  Lightning,
  ArrowRight,
  WhatsappLogo,
} from "@phosphor-icons/react";
import MagneticButton from "@/components/MagneticButton";
import PilotCarIcon from "@/components/PilotCarIcon";
import RunwayDivider from "@/components/RunwayDivider";
import SEOHead from "@/components/SEOHead";

const SITE_URL = "https://www.rijschool-autopilot.nl";
const WHATSAPP_NUMBER = "31620933773";

// Single source of truth — used here AND on /spoed
export const RATES = {
  lesson: { label: "Rijles (60 min)", price: 60, unit: "p/uur" },
  exam: { label: "Praktijkexamen", price: 300 },
  ttt: { label: "Tussentijdse Toets (TTT)", price: 275 },
  bnor: { label: "BNOR examen", price: 350 },
  faalangst: { label: "Faalangstexamen", price: 385 },
};

const fmt = (n) =>
  `€${n.toLocaleString("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 0 })},-`;

const TARIEF_KAARTEN = [
  {
    id: "lesson",
    overline: "Per losse rijles",
    title: "Rijles",
    sub: "60 minuten, inclusief ophalen in de regio",
    price: RATES.lesson.price,
    unit: "/ uur",
    points: [
      "Persoonlijke aanpak, rustig tempo",
      "Ophaalservice in Eindhoven & Helmond",
      "Notities in je digitale leskaart",
    ],
    highlight: false,
  },
  {
    id: "exam",
    overline: "CBR",
    title: "Praktijkexamen",
    sub: "Aanmelding en reservering via het CBR",
    price: RATES.exam.price,
    unit: "per examen",
    points: [
      "Inclusief reservering bij het CBR",
      "Voorbereiding tijdens je laatste lessen",
      "Begeleiding op de examenochtend",
    ],
    highlight: true,
  },
  {
    id: "ttt",
    overline: "CBR",
    title: "Tussentijdse Toets",
    sub: "Verplichte tussentoets — niet slagen/zakken",
    price: RATES.ttt.price,
    unit: "per toets",
    points: [
      "Een echte CBR-examinator beoordeelt je rit",
      "Feedback in plaats van een eindcijfer",
      "Strategisch ingepland in je traject",
    ],
    highlight: false,
  },
  {
    id: "bnor",
    overline: "CBR · aangepast examen",
    title: "BNOR examen",
    sub: "Examen op maat — extra tijd en rust",
    price: RATES.bnor.price,
    unit: "per examen",
    points: [
      "Aangepaste examenduur voor wie meer tijd nodig heeft",
      "Wij regelen de aanmelding bij het CBR",
      "Persoonlijke voorbereiding",
    ],
    highlight: false,
  },
  {
    id: "faalangst",
    overline: "CBR · faalangstexamen",
    title: "Faalangstexamen",
    sub: "Officieel CBR-examen met extra begeleiding",
    price: RATES.faalangst.price,
    unit: "per examen",
    points: [
      "Voor wie last heeft van examenstress",
      "Extra rust en duidelijke uitleg",
      "Aanmelding via ons bij het CBR",
    ],
    highlight: false,
  },
];

const TarievenPage = () => {
  const waMsg = encodeURIComponent(
    "Hoi! Ik heb een vraag over de tarieven van AutoPilot."
  );

  return (
    <>
      <SEOHead
        title="Tarieven rijles & examen — Rijschool AutoPilot Eindhoven & Helmond"
        description="Heldere tarieven van Rijschool AutoPilot. Rijles €60 per uur · Praktijkexamen €300 · TTT €275 · BNOR €350 · Faalangstexamen €385. Transparant en zonder verborgen kosten."
        keywords="tarieven rijschool eindhoven, prijs rijles helmond, kosten praktijkexamen, kosten TTT, BNOR tarief, faalangstexamen kosten, rijles tarieven autopilot"
        lang="nl"
        canonical={`${SITE_URL}/tarieven`}
        ogLocale="nl_NL"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "PriceSpecification",
          name: "Tarieven Rijschool AutoPilot",
          priceCurrency: "EUR",
          description:
            "Rijles €60/uur, Praktijkexamen €300, TTT €275, BNOR €350, Faalangstexamen €385.",
        }}
      />

      {/* Banner */}
      <section
        className="relative pt-40 pb-12 px-6 md:px-12 overflow-hidden noise"
        data-testid="tarieven-banner"
      >
        <div className="absolute inset-0 -z-0">
          <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-[rgba(212,175,55,0.08)] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="runway-stripes w-16 block" />
            <span className="overline">Tarieven</span>
          </div>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl tracking-tight font-light text-white leading-[1.05]"
            data-testid="tarieven-title"
          >
            Heldere prijzen,
            <br />
            <span className="font-display-italic text-gold text-6xl md:text-7xl lg:text-8xl">
              geen verrassingen.
            </span>
          </h1>
          <p className="text-lg text-silver mt-6 max-w-2xl leading-relaxed">
            Bij Rijschool AutoPilot werken we met transparante uurtarieven en
            heldere examen­prijzen. Geen verborgen kosten, geen kleine lettertjes.
            Liever een compleet pakket of een spoedtraject? Bekijk{" "}
            <Link to="/pakketten" className="text-gold link-underline">
              onze pakketten
            </Link>{" "}
            of bereken je{" "}
            <Link to="/spoed" className="text-gold link-underline">
              spoedopleiding
            </Link>
            .
          </p>
        </div>
      </section>

      <RunwayDivider />

      {/* Tarief­kaarten */}
      <section
        className="py-16 lg:py-20 px-6 md:px-12"
        data-testid="tarieven-grid-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TARIEF_KAARTEN.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`relative h-full ${
                  t.highlight ? "gold-tracer" : ""
                }`}
                data-testid={`tarief-card-${t.id}`}
              >
                <div
                  className={`relative p-7 lg:p-8 h-full flex flex-col gap-4 ${
                    t.highlight ? "card-highlight" : "card"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="overline mb-2">{t.overline}</div>
                      <h3 className="text-xl text-white tracking-tight font-medium">
                        {t.title}
                      </h3>
                    </div>
                    <PilotCarIcon
                      size={26}
                      weight={t.highlight ? "fill" : "duotone"}
                      className="text-gold flex-shrink-0"
                    />
                  </div>

                  <div className="text-xs text-silver/80 leading-relaxed">
                    {t.sub}
                  </div>

                  <div className="flex items-baseline gap-2 pt-2 border-t border-white/8">
                    <span className="text-4xl font-extralight text-white leading-none">
                      {fmt(t.price)}
                    </span>
                    <span className="text-xs text-silver/70">{t.unit}</span>
                  </div>

                  <ul className="text-sm text-silver space-y-2 mt-1">
                    {t.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <CheckCircle
                          size={16}
                          weight="duotone"
                          className="text-gold flex-shrink-0 mt-0.5"
                        />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Belangrijke info */}
          <div
            className="mt-10 card p-6 flex items-start gap-4"
            data-testid="tarieven-info"
          >
            <Info size={20} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
            <div className="text-sm text-silver leading-relaxed">
              <p className="text-white mb-2 font-medium">Goed om te weten</p>
              <ul className="space-y-1.5 list-disc list-inside marker:text-gold">
                <li>Voor jouw eerste les bieden we een <span className="text-white">gratis kennismakings-/proefles</span> aan — laagdrempelig en zonder verplichtingen.</li>
                <li>Bij een pakket profiteer je van een voordeligere prijs per les. Zie de <Link to="/pakketten" className="text-gold link-underline">pakketten­pagina</Link>.</li>
                <li>Annulering minimaal 24 uur van tevoren, anders wordt de gereserveerde tijd als gereden les beschouwd.</li>
                <li>Prijzen kunnen wijzigen bij CBR-tariefaanpassingen.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <RunwayDivider />

      {/* CTA */}
      <section className="py-20 px-6 md:px-12" data-testid="tarieven-cta">
        <div className="max-w-4xl mx-auto text-center">
          <div className="overline mb-4 flex items-center justify-center gap-2">
            <Lightning size={14} weight="fill" /> Klaar om te starten?
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-6">
            Vragen over een tarief of{" "}
            <span className="font-display-italic text-gold text-4xl md:text-5xl lg:text-6xl">
              direct beginnen?
            </span>
          </h2>
          <p className="text-silver leading-relaxed mb-10 max-w-2xl mx-auto">
            Stuur ons een berichtje op WhatsApp — je krijgt meestal binnen een uur
            antwoord. Liever een spoedtraject samenstellen met onze calculator?
            Dat kan ook.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid="tarieven-cta-wa"
              >
                <WhatsappLogo size={18} weight="bold" /> Stel je vraag via WhatsApp
              </a>
            </MagneticButton>
            <MagneticButton>
              <Link to="/spoed" className="btn-ghost" data-testid="tarieven-cta-spoed">
                Spoedopleiding berekenen
                <ArrowRight size={16} weight="bold" className="anim-arrow" />
              </Link>
            </MagneticButton>
          </div>
        </div>
      </section>
    </>
  );
};

export default TarievenPage;
